import axios from 'axios'
import { concurrencyService } from '../../common/services/concurrency.service'
import { reportDataFileService } from '../../reports/dataset/report-data-file.service'
import { BaseInterceptor } from '../base'

/**
 * Intercepts requests for report block-data to retrieve the data from one-or-more data (json) files co-located with the webapp
 */
export class BlockDataRequestInterceptor extends BaseInterceptor {
  constructor (dataFolder) {
    super({
      method: 'get',
      path: 'api/report/:reportId/block-data/:blockKey'
    })

    this.dataFolder = dataFolder
  }

  /**
   * On Intercept:
   * 1. Get the filenames of the data files
   * 2. Fetch the data files
   * 3. Combined the result sets from the files into an object URL
   * 4. Update the URL of the request and return the config
   *
   * @param {object} ctxt Request context
   * @param {object} ctxt.config Request config
   * @param {object} ctxt.config.params.strata Requested strata
   * @param {object} ctxt.config.params.slider Requested slider
   * @param {object} ctxt.config.params.filters Requested filters
   * @param {object} params Contextual params
   * @param {object} params.report Report
   * @param {object} params.dataKey Report data key
   * @returns {object} Request config
   */
  async onIntercept ({ config }, { report } = {}) {
    const { strata, slider, filters, dataKey } = config.params
    const filenames = reportDataFileService.getFilenames(
      report,
      strata,
      slider,
      filters,
      dataKey
    )

    const data = (filenames.length > 0) ? await this.fetchData(filenames) : []

    // Create a new object url containing the combined file data
    const url = URL.createObjectURL(
      new Blob([
        JSON.stringify(data)
      ], {
        type: 'application/json'
      })
    )

    return {
      ...config,
      method: 'get',
      url,
      params: null
    }
  }

  /**
   * Fetch the all files and combined the data from each file
   *
   * @param {string[]} filenames Filenames
   * @param {object} options Fetch options
   * @param {Number} options.concurrency Concurrent requests
   * @returns {string[][]} Combined data
   */
  async fetchData (filenames, { concurrency = 10 } = {}) {
    const data = []

    const files = await concurrencyService.map(
      filenames,
      (filename) => this.fetchFile(this.dataFolder, filename),
      {
        concurrency
      }
    )

    // push the header from the first row of the first file with data
    const header = this.extractHeader(files)
    if (header) {
      data.push(header)
    }

    // push all non-header data
    data.push(
      ...files.filter((file) => file.length > 0).map((file) => file.slice(1)).flat()
    )

    return data
  }

  /**
   * Fetch a single file, return an empty array if the file is missing
   *
   * @param {string} folder Name of the folder
   * @param {string} filename Name of the file
   * @returns {string[][]} Single file's data
   */
  async fetchFile (folder, filename) {
    try {
      const response = await axios.get(`${folder}/${filename}`)
      return response?.data || []
    } catch (error) {
      if (error.response.status !== 404) {
        throw error
      }
      return []
    }
  }

  /**
   * Extract header row from the first file with data
   *
   * @param {string[][]} files
   * @returns {string[]} Header row
   */
  extractHeader (files) {
    const fileWithData = files.find((file) => file.length > 0)
    if (fileWithData) {
      return fileWithData[0]
    }
  }
}
